"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.UsersListResponse = exports.UserSummary = exports.UserRoleRef = exports.ResetUserPasswordRequest = exports.UpdateUserRequest = exports.CreateUserRequest = void 0;
const zod_1 = require("zod");
/**
 * Platform user management (admin-only) — full CRUD.
 *
 * Users are the BOS operators who log in to the admin UI (distinct from
 * cashiers, who authenticate at the terminal with a PIN). A super-admin
 * can create, edit, deactivate/reactivate, reset the password of, and
 * assign roles to any user. There is no hard delete — users are
 * referenced by immutable audit entries, so deactivation is the
 * off-boarding mechanism. All endpoints are super-admin-gated (TRS §2 —
 * platform administration).
 */
/** POST /users — create a platform user. */
exports.CreateUserRequest = zod_1.z
    .object({
    email: zod_1.z.string().email().max(255),
    displayName: zod_1.z.string().min(1).max(120),
    /**
     * Initial password (Argon2id-hashed server-side; never stored or
     * returned in plaintext). The operator should rotate it on first
     * login — Phase 2 adds a forced-rotation flag.
     */
    password: zod_1.z.string().min(8).max(200),
    /** Grant platform super-admin (implicitly holds every permission). */
    isSuperAdmin: zod_1.z.boolean().default(false),
    /**
     * Optional initial role set (role ids). Same semantics + validation
     * as the PATCH `roleIds` — unknown ids are rejected 409 `UNKNOWN_ROLE`.
     * Omit or pass `[]` to create the user with no roles.
     */
    roleIds: zod_1.z.array(zod_1.z.string()).optional(),
})
    .strict();
/**
 * PATCH /users/:id — partial update. Omitted fields are left untouched.
 *
 * `roleIds` is a full replacement of the user's role set (not a merge);
 * pass `[]` to clear all roles. Server-side rails reject self-lockout
 * (deactivating or demoting yourself) and removing the last active
 * super-admin (409 `SELF_DEACTIVATION` / `SELF_DEMOTION` /
 * `LAST_SUPER_ADMIN`).
 */
exports.UpdateUserRequest = zod_1.z
    .object({
    email: zod_1.z.string().email().max(255).optional(),
    displayName: zod_1.z.string().min(1).max(120).optional(),
    isSuperAdmin: zod_1.z.boolean().optional(),
    /** false = deactivate (blocks login; the JWT guard rejects existing tokens). */
    isActive: zod_1.z.boolean().optional(),
    /** Full replacement of the user's role set (role ids). */
    roleIds: zod_1.z.array(zod_1.z.string()).optional(),
})
    .strict();
/** POST /users/:id/password — super-admin sets a new password directly. */
exports.ResetUserPasswordRequest = zod_1.z
    .object({
    password: zod_1.z.string().min(8).max(200),
})
    .strict();
/** A role attached to a user (read shape). */
exports.UserRoleRef = zod_1.z.object({
    id: zod_1.z.string(),
    name: zod_1.z.string(),
});
/** Read shape — never includes the password hash. */
exports.UserSummary = zod_1.z.object({
    id: zod_1.z.string(),
    email: zod_1.z.string(),
    displayName: zod_1.z.string(),
    isSuperAdmin: zod_1.z.boolean(),
    isActive: zod_1.z.boolean(),
    roles: zod_1.z.array(exports.UserRoleRef),
    lastLoginAt: zod_1.z.string().datetime().nullable(),
    createdAt: zod_1.z.string().datetime(),
});
exports.UsersListResponse = zod_1.z.object({
    items: zod_1.z.array(exports.UserSummary),
    total: zod_1.z.number().int().min(0),
});
//# sourceMappingURL=users.js.map